/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu May 25 2023
 **/

import { DependencyList, useEffect, useRef } from "react";

export default function useOutsideClickEffect<E extends HTMLElement>(
	callback: (ev?: MouseEvent | TouchEvent) => void,
	deps: DependencyList = []
) {
	const ref = useRef<E>(null);
	const clickStateRef = useRef(false);

	/**
	 * portal로 렌더링된 요소를 통해 특정 컴포넌트의 상태를 변경한 후 외부 클릭시 해당 상태를 초기화할 때
	 * portal은 무조건 외부 요소이기 때문에 클릭한 시점의 타겟과 클릭을 마친 시점의 타겟이 일치하는지 판단하기 위해 들어간 코드입니다.
	 */
	let clickStartTarget: Element | null = null;

	const handler = (ev: MouseEvent | TouchEvent) => {
		const target = ev.target as Element;
		if (!clickStartTarget || clickStateRef.current === false) return;
		if (!target.contains(clickStartTarget)) return;

		if (!ref.current?.contains(target)) {
			callback(ev);
		}
		clickStartTarget = null;
		endClick();
	};

	const startClick = (ev: MouseEvent) => {
		const target = ev.target as Element;
		clickStartTarget = target;
		clickStateRef.current = true;
	};

	const endClick = () => {
		clickStateRef.current = false;
	};

	useEffect(() => {
		if (!ref.current) return;
		document.addEventListener("mousedown", startClick);
		document.addEventListener("mouseup", handler);
		document.addEventListener("touchstart", handler);
		return () => {
			document.removeEventListener("mousedown", startClick);
			document.removeEventListener("mouseup", handler);
			document.removeEventListener("touchstart", handler);
		};
	}, [...deps, handler]);

	return { ref };
}
